/**
 * Common type definitions for the ATA IT Contract Manager
 *
 * This file contains shared TypeScript types and interfaces used throughout
 * the application. These types should eventually be replaced with types
 * generated from the OpenAPI specification or imported from the API client.
 */

/**
 * Contract status enumeration
 * Matches backend ContractStatus enum
 */
export const ContractStatus = {
  Draft: 'Draft',
  Active: 'Active',
  PendingRenewal: 'PendingRenewal',
  Expired: 'Expired',
  Terminated: 'Terminated',
} as const;
export type ContractStatus = (typeof ContractStatus)[keyof typeof ContractStatus];

/** True when the value is one of the ContractStatus strings (used to validate URL search params) */
export function isContractStatus(value: unknown): value is ContractStatus {
  return typeof value === 'string' && (Object.values(ContractStatus) as string[]).includes(value);
}

/**
 * Contract type enumeration
 * Matches backend ContractType enum
 */
export const ContractType = {
  SaaSSubscription: 'SaaSSubscription',
  PerpetualLicense: 'PerpetualLicense',
  MaintenanceAgreement: 'MaintenanceAgreement',
  EnterpriseAgreement: 'EnterpriseAgreement',
  VolumeLicensing: 'VolumeLicensing',
} as const;
export type ContractType = (typeof ContractType)[keyof typeof ContractType];

/**
 * Payment frequency values the API accepts (a string column constrained by the validators)
 */
export const PaymentFrequency = {
  Monthly: 'Monthly',
  Quarterly: 'Quarterly',
  Annual: 'Annual',
  OneTime: 'OneTime',
  Other: 'Other',
} as const;
export type PaymentFrequency = (typeof PaymentFrequency)[keyof typeof PaymentFrequency];

/** Display labels for PaymentFrequency values */
export const PAYMENT_FREQUENCY_LABELS: Record<PaymentFrequency, string> = {
  Monthly: 'Monthly',
  Quarterly: 'Quarterly',
  Annual: 'Annual',
  OneTime: 'One-time',
  Other: 'Other',
};

/** Label for a stored payment frequency; a legacy value outside the set is shown as is */
export const paymentFrequencyLabel = (value: string | null | undefined): string | null =>
  value ? (PAYMENT_FREQUENCY_LABELS[value as PaymentFrequency] ?? value) : null;

/**
 * Vendor status enumeration
 */
export type VendorStatus = 'Active' | 'Inactive' | 'Pending';

/**
 * License status enumeration
 */
export type LicenseStatus = 'Active' | 'Expired' | 'Expiring Soon';

/**
 * License type enumeration
 *
 * Defines the various licensing models supported by the system
 */
export const LicenseType = {
  /** Per-user licensing - one license per user */
  PerUser: 'PerUser',
  /** Per-seat licensing - fixed number of seats */
  PerSeat: 'PerSeat',
  /** Concurrent licensing - limited simultaneous users */
  Concurrent: 'Concurrent',
  /** Site license - unlimited users at a location */
  Site: 'Site',
  /** Enterprise license - organization-wide unlimited */
  Enterprise: 'Enterprise',
  /** Consumption-based - pay per usage */
  ConsumptionBased: 'ConsumptionBased',
  /** Named user - specific identified users */
  NamedUser: 'NamedUser',
} as const;
export type LicenseType = (typeof LicenseType)[keyof typeof LicenseType];

/**
 * License compliance status
 *
 * Indicates whether license usage is within allowed limits
 */
export const LicenseComplianceStatus = {
  /** Usage within limits */
  Compliant: 'Compliant',
  /** Usage approaching limits (>80%) */
  Warning: 'Warning',
  /** Usage exceeds limits */
  NonCompliant: 'NonCompliant',
  /** Unknown or unable to determine */
  Unknown: 'Unknown',
} as const;
export type LicenseComplianceStatus = (typeof LicenseComplianceStatus)[keyof typeof LicenseComplianceStatus];

/**
 * Contract list item interface
 *
 * Lightweight DTO for displaying contracts in lists and grids
 */
export interface ContractListItem {
  /** Unique contract identifier */
  id: string;
  /** Contract title */
  title: string;
  /** Unique contract number */
  contractNumber: string;
  /** Associated vendor ID */
  vendorId: string;
  /** Vendor name for display */
  vendorName: string;
  /** Contract type */
  contractType: ContractType;
  /** Contract status */
  status: ContractStatus;
  /** Contract start date */
  startDate: string;
  /** Contract end date */
  endDate: string;
  /** Optional renewal date */
  renewalDate?: string;
  /** Total contract value */
  totalContractValue: number;
  /** Annual contract value */
  annualContractValue: number;
  /** Currency code (e.g., USD, EUR) */
  currency: string;
  /** Owner user ID, null when the contract has no owner */
  ownerUserId?: string | null;
  /** Owner display name, null when the contract has no owner */
  ownerName?: string | null;
  /** Department ID, null when the contract has no department */
  departmentId?: string | null;
  /** Department name, null when the contract has no department */
  departmentName?: string | null;
  /** Whether contract auto-renews */
  autoRenews: boolean;
  /** Days of notice required before the end date */
  noticeRequiredDays: number;
  /** Last day to give notice (end date minus notice days); null when no notice is required */
  noticeDeadline?: string | null;
  /** Whole days until the notice deadline (negative once passed); null when no notice is required */
  daysUntilNoticeDeadline?: number | null;
  /** Seats the contract pays for (active licenses' total quantity, else active allocations); null without seat data */
  seatCount?: number | null;
  /** Annual value per seat rounded to cents; null without seat data */
  costPerUser?: number | null;
  /** Number of associated licenses */
  licenseCount: number;
  /** Number of associated documents */
  documentCount: number;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
}

/**
 * Contract detail interface
 *
 * Complete DTO with all contract information
 */
export interface ContractDetail {
  /** Unique contract identifier */
  id: string;
  /** Contract title */
  title: string;
  /** Unique contract number */
  contractNumber: string;
  /** Associated vendor ID */
  vendorId: string;
  /** Vendor name for display */
  vendorName: string;
  /** Contract type */
  contractType: ContractType;
  /** Contract status */
  status: ContractStatus;
  /** Contract start date */
  startDate: string;
  /** Contract end date */
  endDate: string;
  /** Optional renewal date */
  renewalDate?: string;
  /** Total contract value */
  totalContractValue: number;
  /** Annual contract value */
  annualContractValue: number;
  /** Currency code (e.g., USD, EUR) */
  currency: string;
  /** Owner user ID, null when the contract has no owner */
  ownerUserId?: string | null;
  /** Owner display name, null when the contract has no owner */
  ownerName?: string | null;
  /** Department ID, null when the contract has no department */
  departmentId?: string | null;
  /** Department name, null when the contract has no department */
  departmentName?: string | null;
  /** Whether contract auto-renews */
  autoRenews: boolean;
  /** Days of notice required before the end date */
  noticeRequiredDays: number;
  /** Length of each renewal in months */
  renewalTermMonths?: number | null;
  /** Payment frequency (one of PaymentFrequency, or a legacy value) */
  paymentFrequency?: string | null;
  /** Parent contract ID when this contract is an amendment */
  parentContractId?: string | null;
  /** Parent contract number for display */
  parentContractNumber?: string | null;
  /** Last day to give notice (end date minus notice days); null when no notice is required */
  noticeDeadline?: string | null;
  /** Whole days until the notice deadline (negative once passed); null when no notice is required */
  daysUntilNoticeDeadline?: number | null;
  /** Contract description */
  description?: string;
  /** Payment terms */
  paymentTerms?: string;
  /** Internal notes */
  notes?: string;
  /** Whether the contract has been archived (soft-deleted) */
  isArchived?: boolean;
  /** Seats the contract pays for (active licenses' total quantity, else active allocations); null without seat data */
  seatCount?: number | null;
  /** Annual value per seat rounded to cents; null without seat data */
  costPerUser?: number | null;
  /** Number of associated licenses */
  licenseCount: number;
  /** Number of associated documents */
  documentCount: number;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
}

/**
 * Create contract DTO
 *
 * Data required to create a new contract
 */
export interface CreateContract {
  /** Contract title */
  title: string;
  /** Unique contract number */
  contractNumber: string;
  /** Associated vendor ID */
  vendorId: string;
  /** Contract type */
  contractType: ContractType;
  /** Contract status */
  status: ContractStatus;
  /** Contract start date (ISO 8601 format) */
  startDate: string;
  /** Contract end date (ISO 8601 format) */
  endDate: string;
  /** Optional renewal date (ISO 8601 format) */
  renewalDate?: string;
  /** Total contract value */
  totalContractValue: number;
  /** Annual contract value */
  annualContractValue: number;
  /** Currency code (e.g., USD, EUR) */
  currency: string;
  /** Owner user ID (optional; must be an existing user) */
  ownerUserId?: string;
  /** Department ID (optional; must be an existing department) */
  departmentId?: string;
  /** Whether contract auto-renews */
  autoRenews: boolean;
  /** Days of notice required before the end date (0 to 365) */
  noticeRequiredDays: number;
  /** Length of each renewal in months (1 to 120) */
  renewalTermMonths?: number;
  /** Payment frequency, one of PaymentFrequency */
  paymentFrequency?: PaymentFrequency;
  /** Parent contract ID when this contract is an amendment */
  parentContractId?: string;
  /** Contract description */
  description?: string;
  /** Payment terms */
  paymentTerms?: string;
  /** Internal notes */
  notes?: string;
}

/**
 * Update contract DTO
 *
 * Data required to update an existing contract
 */
export interface UpdateContract {
  /** Contract title */
  title: string;
  /** Unique contract number */
  contractNumber: string;
  /** Associated vendor ID */
  vendorId: string;
  /** Contract type */
  contractType: ContractType;
  /** Contract status */
  status: ContractStatus;
  /** Contract start date (ISO 8601 format) */
  startDate: string;
  /** Contract end date (ISO 8601 format) */
  endDate: string;
  /** Optional renewal date (ISO 8601 format) */
  renewalDate?: string;
  /** Total contract value */
  totalContractValue: number;
  /** Annual contract value */
  annualContractValue: number;
  /** Currency code (e.g., USD, EUR) */
  currency: string;
  /** Owner user ID (optional; must be an existing user) */
  ownerUserId?: string;
  /** Department ID (optional; must be an existing department) */
  departmentId?: string;
  /** Whether contract auto-renews */
  autoRenews: boolean;
  /** Days of notice required before the end date (0 to 365) */
  noticeRequiredDays: number;
  /** Length of each renewal in months (1 to 120) */
  renewalTermMonths?: number;
  /** Payment frequency, one of PaymentFrequency */
  paymentFrequency?: PaymentFrequency;
  /** Parent contract ID when this contract is an amendment */
  parentContractId?: string;
  /** Contract description */
  description?: string;
  /** Payment terms */
  paymentTerms?: string;
  /** Internal notes */
  notes?: string;
}

/**
 * Contract entity interface (legacy)
 *
 * @deprecated Use ContractListItem or ContractDetail instead
 */
export interface Contract {
  id: string;
  name: string;
  vendorId: string;
  vendor: Vendor;
  startDate: Date;
  endDate: Date;
  status: ContractStatus;
  value: number;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Vendor entity interface
 *
 * TODO: Replace with type from API client
 */
export interface Vendor {
  id: string;
  name: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  address?: string;
  status: VendorStatus;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * License list item interface
 *
 * Lightweight DTO for displaying licenses in lists and grids
 */
export interface LicenseListItem {
  /** Unique license identifier */
  id: string;
  /** Software/product name */
  productName: string;
  /** Product version */
  version?: string;
  /** License type */
  licenseType: LicenseType;
  /** Associated contract ID */
  contractId: string;
  /** Contract name for display */
  contractName: string;
  /** Total number of licenses purchased */
  totalQuantity: number;
  /** Number of licenses currently allocated */
  allocatedQuantity: number;
  /** Number of available licenses (total - allocated) */
  availableQuantity: number;
  /** Utilization percentage (0-100) */
  utilizationPercentage: number;
  /** Cost per license unit */
  costPerUnit: number;
  /** Currency code */
  currency: string;
  /** License expiration date */
  expirationDate?: string;
  /** License status */
  isActive: boolean;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
}

/**
 * License detail interface
 *
 * Complete DTO with all license information including allocations
 */
export interface LicenseDetail {
  /** Unique license identifier */
  id: string;
  /** Software/product name */
  productName: string;
  /** Product version */
  version?: string;
  /** License type */
  licenseType: LicenseType;
  /** Associated contract ID */
  contractId: string;
  /** Contract name for display */
  contractName: string;
  /** Total number of licenses purchased */
  totalQuantity: number;
  /** Number of licenses currently allocated */
  allocatedQuantity: number;
  /** Number of available licenses (total - allocated) */
  availableQuantity: number;
  /** Utilization percentage (0-100) */
  utilizationPercentage: number;
  /** Cost per license unit */
  costPerUnit: number;
  /** Currency code */
  currency: string;
  /** License expiration date */
  expirationDate?: string;
  /** License key or activation code */
  licenseKey?: string;
  /** Additional notes */
  notes?: string;
  /** License status */
  isActive: boolean;
  /** Compliance status */
  complianceStatus: LicenseComplianceStatus;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
}

/**
 * Create license DTO
 *
 * Data required to create a new license
 */
export interface CreateLicense {
  /** Associated contract ID */
  contractId: string;
  /** Software/product name */
  productName: string;
  /** Product version */
  version?: string;
  /** License type */
  licenseType: LicenseType;
  /** Total number of licenses purchased */
  totalQuantity: number;
  /** Cost per license unit */
  costPerUnit: number;
  /** License expiration date (ISO 8601 format) */
  expirationDate?: string;
  /** License key or activation code */
  licenseKey?: string;
  /** Additional notes */
  notes?: string;
  /** License status */
  isActive: boolean;
}

/**
 * Update license DTO
 *
 * Data required to update an existing license
 */
export interface UpdateLicense {
  /** Associated contract ID */
  contractId?: string;
  /** Software/product name */
  productName?: string;
  /** Product version */
  version?: string;
  /** License type */
  licenseType?: LicenseType;
  /** Total number of licenses purchased */
  totalQuantity?: number;
  /** Cost per license unit */
  costPerUnit?: number;
  /** License expiration date (ISO 8601 format) */
  expirationDate?: string;
  /** License key or activation code */
  licenseKey?: string;
  /** Additional notes */
  notes?: string;
  /** License status */
  isActive?: boolean;
}

/**
 * What a license allocation is assigned to
 *
 * Mirrors the values the API stores in `LicenseAllocation.AllocatedToType`.
 */
export const AllocationType = {
  User: 'User',
  Department: 'Department',
  CostCenter: 'CostCenter',
} as const;

export type AllocationType = (typeof AllocationType)[keyof typeof AllocationType];

/**
 * License allocation interface
 *
 * Mirrors the API's `LicenseAllocationDto`: a block of `quantity` units assigned to a user
 * (email), a department or a cost center.
 */
export interface LicenseAllocation {
  /** Unique allocation identifier */
  id: string;
  /** Associated license ID */
  licenseId: string;
  /** Product name of the license, for display */
  productName?: string;
  /** Email, department name or cost center code the units are assigned to */
  allocatedTo: string;
  /** What `allocatedTo` names */
  allocatedToType: AllocationType;
  /** Number of units in this allocation */
  quantity: number;
  /** Allocation date */
  allocatedDate: string;
  /** Deallocation date; absent while the allocation is active (the API omits null fields) */
  deallocatedDate?: string;
  /** Additional notes */
  notes?: string;
  /** Allocation status (no deallocation date) */
  isActive: boolean;
  /** Creation timestamp */
  createdAt: string;
  /** Who created the allocation */
  createdBy?: string;
  /** Last update timestamp */
  updatedAt: string;
  /** Who last updated the allocation */
  updatedBy?: string;
}

/**
 * Create license allocation DTO
 *
 * Mirrors the API's `CreateAllocationDto`. The license is named in the URL, not the body.
 */
export interface CreateAllocation {
  /** Email, department name or cost center code (max 200 characters) */
  allocatedTo: string;
  /** What `allocatedTo` names */
  allocatedToType: AllocationType;
  /** Number of units, at least 1 and at most the license's available quantity */
  quantity: number;
  /** Additional notes (max 2000 characters) */
  notes?: string;
}

/**
 * Outcome of a successful CSV import of allocations
 *
 * Mirrors the API's `ImportAllocationsResultDto` (`POST /licenses/{id}/allocations/import`).
 */
export interface AllocationImportResult {
  /** Non-blank data rows in the file */
  rowCount: number;
  /** Allocations created */
  created: number;
  /** Rows that matched an active allocation with the same quantity and were left alone */
  skipped: number;
  /** User allocations removed because they were absent from the file (replace mode) */
  deallocated: number;
}

/**
 * One problem in a rejected import file
 *
 * `row` counts the header as row 1, as a spreadsheet does. Mirrors the API's `ImportRowErrorDto`.
 */
export interface AllocationImportRowError {
  /** Row of the file (header = 1) */
  row: number;
  /** What is wrong with the row */
  message: string;
}

/**
 * License compliance overview
 *
 * Summary of license compliance across the organization
 */
export interface LicenseComplianceOverview {
  /** Total number of license pools */
  totalLicensePools: number;
  /** Number of compliant license pools */
  compliantCount: number;
  /** Number of license pools with warnings */
  warningCount: number;
  /** Number of non-compliant license pools */
  nonCompliantCount: number;
  /** Overall compliance rate (0-100) */
  overallComplianceRate: number;
  /** Total cost of all licenses */
  totalLicenseCost: number;
  /** Total licenses purchased */
  totalLicensesPurchased: number;
  /** Total licenses allocated */
  totalLicensesAllocated: number;
  /** Overall utilization rate (0-100) */
  overallUtilizationRate: number;
}

/**
 * License entity interface (legacy)
 *
 * @deprecated Use LicenseListItem or LicenseDetail instead
 */
export interface License {
  id: string;
  productName: string;
  licenseType: LicenseType;
  contractId: string;
  contract: Contract;
  totalLicenses: number;
  allocatedLicenses: number;
  status: LicenseStatus;
  licenseKey?: string;
  expiryDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Dashboard statistics interface
 */
export interface DashboardStats {
  totalContracts: number;
  activeContracts: number;
  expiringContracts: number;
  totalVendors: number;
  activeVendors: number;
  totalLicenses: number;
  allocatedLicenses: number;
  utilizationPercentage: number;
}

/**
 * Dashboard summary statistics
 *
 * Comprehensive statistics for the dashboard overview including
 * contract counts, total values, compliance metrics, and expiration data.
 */
export interface DashboardSummary {
  /** Total number of active contracts */
  totalActiveContracts: number;
  /** Total value of all contracts */
  totalContractValue: number;
  /** Total annual value of all active contracts */
  totalAnnualValue: number;
  /** Total number of vendors */
  totalVendors: number;
  /** Total number of licenses across all contracts */
  totalLicenses: number;
  /** Total number of allocated licenses */
  totalAllocatedLicenses: number;
  /** Compliance rate as a percentage (0-100) of licenses that are properly managed */
  complianceRate: number;
  /** Number of contracts expiring in the current month */
  contractsExpiringThisMonth: number;
  /** Number of contracts expiring in the current quarter */
  contractsExpiringThisQuarter: number;
  /** Contracts inside their notice window (status PendingRenewal, not archived) */
  contractsInNoticeWindow: number;
  /** Annual value of the contracts inside their notice window */
  noticeWindowAnnualValue: number;
}

/**
 * Upcoming contract renewal information
 *
 * Contains details about contracts that are approaching renewal,
 * used to display upcoming renewal alerts and tables.
 */
export interface UpcomingRenewal {
  /** Unique contract identifier */
  contractId: string;
  /** Contract name */
  contractName: string;
  /** Vendor name */
  vendorName: string;
  /** Contract end date: an ISO string at UTC midnight (JSON never carries a Date); render with formatCalendarDate */
  endDate: string;
  /** Number of days until contract expires */
  daysUntil: number;
  /** Annual contract value */
  annualContractValue: number;
  /** Whether the contract auto-renews */
  autoRenews: boolean;
}

/**
 * Monthly spending trend data point
 *
 * Represents spending data for a specific month,
 * used in line charts to visualize spending trends over time.
 */
export interface SpendTrend {
  /** Month label in YYYY-MM format */
  month: string;
  /** Total spending amount for the month */
  totalSpend: number;
  /** Number of active contracts during the month */
  contractCount: number;
}

/**
 * Contract status distribution data
 *
 * Represents the count of contracts in each status category,
 * used in pie charts to visualize contract status distribution.
 */
export interface ContractsByStatus {
  /** Contract status (Active, Expired, Pending, Cancelled) */
  status: ContractStatus;
  /** Number of contracts with this status */
  count: number;
  /** Percentage of total contracts (0-100) */
  percentage: number;
}

/**
 * License utilization statistics
 *
 * Provides aggregated data about license allocation and utilization,
 * helping identify over-allocation and under-utilization.
 */
export interface LicenseUtilization {
  /** Total number of licenses purchased */
  totalPurchased: number;
  /** Total number of licenses allocated to users/departments */
  totalAllocated: number;
  /** Total number of licenses available for allocation */
  totalAvailable: number;
  /** Utilization percentage (0-100) */
  utilizationPercentage: number;
  /** Number of licenses that are over-allocated (allocated > purchased) */
  overAllocatedCount: number;
  /** Number of license pools with less than 50% utilization */
  underUtilizedCount: number;
}

/**
 * API error response interface
 */
export interface ApiError {
  message: string;
  statusCode: number;
  errors?: Record<string, string[]>;
}

/**
 * Paginated response interface
 */
export interface PaginatedResponse<T> {
  data: T[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Paged result interface
 *
 * Generic wrapper for paginated API responses with metadata
 */
export interface PagedResult<T> {
  /** Array of items for the current page */
  items: T[];
  /** Current page number (1-indexed) */
  pageNumber: number;
  /** Number of items per page */
  pageSize: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items across all pages */
  totalCount: number;
  /** Whether there is a next page */
  hasNextPage: boolean;
  /** Whether there is a previous page */
  hasPreviousPage: boolean;
}

/**
 * Query filter parameters
 */
export interface QueryFilters {
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Vendor list item interface
 *
 * Lightweight vendor representation for list views
 */
export interface VendorListItem {
  /** Unique vendor identifier */
  id: string;
  /** Vendor company name */
  name: string;
  /** Website URL */
  website?: string;
  /** Primary contact person name */
  primaryContactName: string;
  /** Primary contact email */
  primaryContactEmail: string;
  /** City */
  city?: string;
  /** Country */
  country?: string;
  /** Vendor criticality rating (1-5) */
  criticalityRating: number;
  /** Vendor status */
  isActive: boolean;
  /** Total number of contracts */
  totalContracts: number;
  /** Number of active contracts */
  activeContracts: number;
  /** Total value of all active contracts */
  totalContractValue: number;
  /** Created timestamp */
  createdAt: string;
  /** Last updated timestamp */
  updatedAt: string;
}

/**
 * Vendor detail interface
 *
 * Complete vendor information including related entities
 */
export interface VendorDetail {
  /** Unique vendor identifier */
  id: string;
  /** Vendor company name */
  name: string;
  /** Vendor description */
  description?: string;
  /** Website URL */
  website?: string;
  /** Primary contact person name */
  primaryContactName: string;
  /** Primary contact email */
  primaryContactEmail: string;
  /** Primary contact phone */
  primaryContactPhone?: string;
  /** Street address */
  address?: string;
  /** City */
  city?: string;
  /** State/Province */
  state?: string;
  /** Country */
  country?: string;
  /** Postal/ZIP code */
  postalCode?: string;
  /** Vendor criticality rating (1-5) */
  criticalityRating: number;
  /** Additional notes */
  notes?: string;
  /** Active status */
  isActive: boolean;
  /** Created timestamp */
  createdAt: string;
  /** Last updated timestamp */
  updatedAt: string;
  /** Associated contracts */
  contracts: VendorContractSummary[];
}

/**
 * Create vendor request interface
 */
export interface CreateVendor {
  /** Vendor company name */
  name: string;
  /** Vendor description */
  description?: string;
  /** Website URL */
  website?: string;
  /** Primary contact person name */
  primaryContactName: string;
  /** Primary contact email */
  primaryContactEmail: string;
  /** Primary contact phone */
  primaryContactPhone?: string;
  /** Street address */
  address?: string;
  /** City */
  city?: string;
  /** State/Province */
  state?: string;
  /** Country */
  country?: string;
  /** Postal/ZIP code */
  postalCode?: string;
  /** Vendor criticality rating (1-5) */
  criticalityRating: number;
  /** Additional notes */
  notes?: string;
  /** Active status */
  isActive: boolean;
}

/**
 * Update vendor request interface
 */
export interface UpdateVendor {
  /** Vendor company name */
  name?: string;
  /** Vendor description */
  description?: string;
  /** Website URL */
  website?: string;
  /** Primary contact person name */
  primaryContactName?: string;
  /** Primary contact email */
  primaryContactEmail?: string;
  /** Primary contact phone */
  primaryContactPhone?: string;
  /** Street address */
  address?: string;
  /** City */
  city?: string;
  /** State/Province */
  state?: string;
  /** Country */
  country?: string;
  /** Postal/ZIP code */
  postalCode?: string;
  /** Vendor criticality rating (1-5) */
  criticalityRating?: number;
  /** Additional notes */
  notes?: string;
  /** Active status */
  isActive?: boolean;
}

/**
 * Vendor contract summary interface
 *
 * Summary of a contract associated with a vendor
 */
export interface VendorContractSummary {
  /** Contract identifier */
  id: string;
  /** Contract number */
  contractNumber: string;
  /** Contract title */
  title: string;
  /** Contract type */
  contractType: ContractType;
  /** Contract start date */
  startDate: string;
  /** Contract end date */
  endDate: string;
  /** Renewal date */
  renewalDate?: string;
  /** Contract status */
  status: ContractStatus;
  /** Total contract value */
  totalContractValue: number;
  /** Annual contract value */
  annualContractValue: number;
  /** Currency code */
  currency: string;
  /** Whether archived */
  isArchived: boolean;
}

/**
 * Document list item interface
 *
 * Represents a document in list views
 */
export interface DocumentListItem {
  /** Document identifier */
  id: string;
  /** Associated contract ID */
  contractId: string;
  /** Original file name */
  originalFileName: string;
  /** MIME content type */
  contentType: string;
  /** File size in bytes */
  fileSize: number;
  /** Document category */
  category: string;
  /** Upload timestamp */
  uploadedAt: string;
  /** User who uploaded */
  uploadedBy: string;
  /** Optional description */
  description?: string;
  /** Comma-separated tags */
  tags?: string;
  /** Status of the latest AI extraction, absent when the document has none */
  extractionStatus?: ExtractionStatus;
  /** Id of the latest AI extraction */
  extractionId?: string;
  /** Message of the latest extraction when it failed */
  extractionError?: string;
  /** Whether the file type can be extracted at all (PDF, PNG, JPEG, DOCX); absent from a pre-#22 API */
  isExtractable?: boolean;
}

/**
 * Lifecycle of an AI document extraction (spec 5.2), matching backend ExtractionStatuses
 */
export const ExtractionStatus = {
  Pending: 'Pending',
  Processing: 'Processing',
  Review: 'Review',
  Applied: 'Applied',
  Rejected: 'Rejected',
  Failed: 'Failed',
  Superseded: 'Superseded',
} as const;
export type ExtractionStatus = (typeof ExtractionStatus)[keyof typeof ExtractionStatus];

/**
 * One extraction without its field rows (GET /contracts/{id}/extractions, /documents/{id}/extraction)
 */
export interface ExtractionSummary {
  id: string;
  /** Absent once the document is deleted */
  documentId?: string;
  documentFileName: string;
  contractId: string;
  status: ExtractionStatus;
  trigger: 'Upload' | 'OnDemand' | 'Backfill';
  schemaType?: 'contract' | 'invoice';
  schemaVersion?: string;
  promptVersion?: string;
  documentKind?: string;
  model?: string;
  servedModel?: string;
  effort?: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd?: number;
  errorCode?: string;
  errorMessage?: string;
  attemptCount: number;
  requestedBy: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

/**
 * A contract's extractions, newest first, and how many wait for review
 */
export interface ContractExtractions {
  pendingReviewCount: number;
  items: ExtractionSummary[];
}

/**
 * Upload document request interface
 */
export interface UploadDocumentRequest {
  /** Associated contract ID */
  contractId: string;
  /** Document category */
  category: string;
  /** Optional description */
  description?: string;
  /** Optional tags */
  tags?: string;
}

/**
 * Notification type enum matching backend NotificationType
 */
export type NotificationType =
  | 'RenewalReminder'
  | 'ExpirationWarning'
  | 'LicenseThreshold'
  | 'SystemAlert'
  | 'NoticeDeadline';

/**
 * User notification
 */
export interface Notification {
  id: string;
  contractId: string | null;
  contractTitle: string | null;
  type: NotificationType;
  typeName: string;
  title: string;
  message: string;
  isRead: boolean;
  readAt: string | null;
  sentAt: string;
  deliveryChannel: string;
}

/**
 * Unread notification count
 */
export interface UnreadCount {
  count: number;
}

/**
 * Spend by vendor for dashboard chart
 */
export interface SpendByVendor {
  vendorId: string;
  vendorName: string;
  annualSpend: number;
  contractCount: number;
}

/**
 * Spend by department/cost center for dashboard chart
 */
export interface SpendByDepartment {
  department: string;
  totalSpend: number;
  recordCount: number;
}

/**
 * How the renewal forecast groups its periods (query `groupBy`)
 */
export const RenewalForecastGrouping = {
  Month: 'month',
  Quarter: 'quarter',
} as const;

export type RenewalForecastGrouping = (typeof RenewalForecastGrouping)[keyof typeof RenewalForecastGrouping];

/** True when the value is one of the grouping strings (used to validate URL search params) */
export function isRenewalForecastGrouping(value: unknown): value is RenewalForecastGrouping {
  return typeof value === 'string' && (Object.values(RenewalForecastGrouping) as string[]).includes(value);
}

/** Window lengths the Renewals page offers */
export const RENEWAL_FORECAST_MONTHS = [6, 12, 24, 36] as const;
export type RenewalForecastMonths = (typeof RENEWAL_FORECAST_MONTHS)[number];

export function isRenewalForecastMonths(value: unknown): value is RenewalForecastMonths {
  return typeof value === 'number' && (RENEWAL_FORECAST_MONTHS as readonly number[]).includes(value);
}

/**
 * One contract inside a renewal forecast period
 */
export interface RenewalForecastContract {
  contractId: string;
  contractNumber: string;
  title: string;
  vendorName: string;
  /** The forecast date (renewal date when set, else end date) as UTC midnight; render with formatCalendarDate */
  date: string;
  /** "RenewalDate" or "EndDate" */
  dateSource: 'RenewalDate' | 'EndDate';
  /** Projected annual amount: the annual value, or the total value over the term in years when the annual value is zero */
  amount: number;
  currency: string;
  autoRenews: boolean;
  /** Omitted by the API when null */
  renewalTermMonths?: number;
  ownerName?: string;
  departmentName?: string;
  status: ContractStatus;
  statusName: string;
}

/**
 * One fiscal month or quarter of the renewal forecast
 */
export interface RenewalForecastPeriod {
  /** Sortable key: yyyy-MM for months, yyyy-Qn for quarters */
  key: string;
  /** "Nov 2026", "Q4 2026", "Q1 FY2027" */
  label: string;
  /** First day (UTC midnight, inclusive) */
  start: string;
  /** Day after the last day (UTC midnight, exclusive) */
  end: string;
  contractCount: number;
  totalAmount: number;
  autoRenewCount: number;
  autoRenewAmount: number;
  decisionNeededCount: number;
  decisionNeededAmount: number;
  contracts: RenewalForecastContract[];
}

/**
 * Renewal forecast: every period in the window, empty ones included, with its contracts
 */
export interface RenewalForecast {
  groupBy: RenewalForecastGrouping;
  fiscalYearStartMonth: number;
  windowStart: string;
  windowEnd: string;
  contractCount: number;
  totalAmount: number;
  periods: RenewalForecastPeriod[];
}

/**
 * A department's budget for one fiscal year (#19)
 */
export interface DepartmentBudget {
  id: string;
  departmentId: string;
  departmentName?: string;
  /** Fiscal year label (the calendar year in which the fiscal year ends) */
  fiscalYear: number;
  amount: number;
  notes?: string;
  updatedAt: string;
  updatedBy: string;
}

/** Body for creating or replacing a department budget */
export interface SaveDepartmentBudget {
  fiscalYear: number;
  amount: number;
  notes?: string;
}

/**
 * One row of the budget-versus-committed report: a department, the Unassigned row
 * (departmentId absent) or the totals. Members the API leaves null are omitted.
 */
export interface BudgetReportRow {
  departmentId?: string;
  departmentName: string;
  departmentCode?: string;
  isActive: boolean;
  budgetId?: string;
  /** Absent when the department has no budget for the year (a budget of 0 is 0) */
  budget?: number;
  budgetNotes?: string;
  /** In-force contracts' value for the months of their term in the year, prorated by month (#57) */
  committed: number;
  /** Contracts with at least one day of their term in the year */
  committedContractCount: number;
  actual: number;
  costRecordCount: number;
  /** Budget minus committed; absent without a budget */
  remaining?: number;
  /** Budget minus actual; absent without a budget */
  variance?: number;
  /** Committed as a percentage of the budget; absent without a budget or with 0 */
  committedPercent?: number;
}

/** Budget versus committed and actual per department for one fiscal year */
export interface BudgetReport {
  fiscalYear: number;
  /** "2026" on a calendar year, "FY2027" otherwise */
  fiscalYearLabel: string;
  /** First day (UTC midnight, inclusive) */
  yearStart: string;
  /** Day after the last day (UTC midnight, exclusive) */
  yearEnd: string;
  rows: BudgetReportRow[];
  totals: BudgetReportRow;
}

/** Fiscal years the budgets route accepts */
export function isBudgetFiscalYear(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 2000 && value <= 2100;
}

/**
 * Cost record types
 */
export type CostRecordType = 'Payment' | 'Invoice' | 'Adjustment' | 'Credit';

/**
 * Cost record associated with a contract
 */
export interface CostRecord {
  id: string;
  contractId: string;
  recordType: CostRecordType;
  amount: number;
  currency: string;
  date: string;
  description: string | null;
  invoiceNumber: string | null;
  departmentId: string | null;
  costCenter: string | null;
  createdAt: string;
  createdBy: string | null;
}

/**
 * Create cost record request
 */
export interface CreateCostRecord {
  recordType: CostRecordType;
  amount: number;
  currency: string;
  date: string;
  description?: string;
  invoiceNumber?: string;
  departmentId?: string;
  costCenter?: string;
}

/**
 * Update cost record request
 */
export interface UpdateCostRecord {
  recordType: CostRecordType;
  amount: number;
  currency: string;
  date: string;
  description?: string;
  invoiceNumber?: string;
  departmentId?: string;
  costCenter?: string;
}

/**
 * Cost summary for a contract
 */
export interface CostSummary {
  totalAmount: number;
  recordCount: number;
  currency: string;
}

/**
 * Milestone types the UI offers. The API keeps DateType a free string (max 50),
 * so a stored value outside this set still displays and stays editable.
 */
export const CONTRACT_DATE_TYPES = ['Start', 'End', 'Renewal', 'PaymentDue', 'PriceEscalation', 'SupportEnd', 'Custom'] as const;
export type ContractDateType = (typeof CONTRACT_DATE_TYPES)[number];

const CONTRACT_DATE_TYPE_LABELS: Record<ContractDateType, string> = {
  Start: 'Start',
  End: 'End',
  Renewal: 'Renewal',
  PaymentDue: 'Payment due',
  PriceEscalation: 'Price escalation',
  SupportEnd: 'Support end',
  Custom: 'Custom',
};

/**
 * Label for a milestone type; any value outside the set is returned unchanged.
 * Own-property check only: the API accepts any string, and an inherited key such as
 * "constructor" would otherwise come back as a non-string and break the render.
 */
export const contractDateTypeLabel = (dateType: string): string =>
  Object.hasOwn(CONTRACT_DATE_TYPE_LABELS, dateType)
    ? CONTRACT_DATE_TYPE_LABELS[dateType as ContractDateType]
    : dateType;

/**
 * Contract milestone (ContractDate row). `date` is a UTC-midnight ISO string;
 * null properties are omitted by the API, so the optional members may be absent.
 */
export interface ContractDate {
  id: string;
  contractId: string;
  dateType: string;
  date: string;
  description?: string;
  reminderDays?: number;
  isReminderSent: boolean;
  daysUntil: number;
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
}

/**
 * Create milestone request
 */
export interface CreateContractDate {
  dateType: string;
  date: string;
  description?: string;
  reminderDays?: number;
}

/**
 * Update milestone request (same shape as create)
 */
export type UpdateContractDate = CreateContractDate;

/**
 * Milestone due within the dashboard window (GET /api/v1/dashboard/upcoming-milestones)
 */
export interface UpcomingMilestone {
  id: string;
  contractId: string;
  contractNumber: string;
  contractTitle: string;
  vendorName: string;
  dateType: string;
  date: string;
  description?: string;
  reminderDays?: number;
  daysUntil: number;
}

/**
 * One row of the dashboard's most-expensive-per-user list (GET /api/v1/dashboard/cost-per-user)
 */
export interface CostPerUserItem {
  contractId: string;
  contractNumber: string;
  contractTitle: string;
  vendorName: string;
  annualContractValue: number;
  currency: string;
  /** Seats the contract pays for; always positive here */
  seatCount: number;
  /** Annual value per seat rounded to cents */
  costPerUser: number;
}

/**
 * User summary for pickers (GET /api/v1/users)
 */
export interface UserSummary {
  /** Unique user identifier */
  id: string;
  /** Display name */
  displayName: string;
  /** Email address */
  email: string;
  /** Whether the account is active; inactive users are returned so a current owner stays visible */
  isActive: boolean;
}

/**
 * Department summary for pickers and filters (GET /api/v1/departments)
 */
export interface DepartmentSummary {
  /** Unique department identifier */
  id: string;
  /** Department name */
  name: string;
  /** Department code or abbreviation */
  code: string;
  /** Whether the department is active */
  isActive: boolean;
}
